import React from "react";
import { Text } from "web/react/emo/text";

import clsx from "clsx";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import * as styles from "./button.css";

export interface ButtonProps {
    className?: string;
    size?: "sm" | "lg";
    width?: "full" | "fit-content";
    icon?: SVGIconName;
    iconPosition?: "left" | "right";
    variant?: "primary" | "secondary" | "tertiary";
    active?: boolean;
    condensed?: boolean;
    inactive?: boolean;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    title: string;
    titleClassName?: string;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
    dataTestId?: string;
}

/**
 * Design System
 * @name Button
 * @version 1.0
 * @design https://www.figma.com/file/VhOHx4tv9uYXZHXAAKPVYf/Design-System?type=design&node-id=3481-30566&t=sJmKH2VN0OACQRby-4
 */
export function Button({
    className,
    size = "lg",
    width = "fit-content",
    icon,
    iconPosition = "left",
    variant = "primary",
    active = false,
    inactive = false,
    href,
    onClick,
    title,
    titleClassName,
    type = "button",
    dataTestId,
    condensed = false,
}: ButtonProps): React.ReactElement {
    const buttonClass = styles.buttonRecipe({
        size,
        width,
        active,
        inactive,
        variant,
        condensed,
        useButtonAnimation: true,
    });

    const content = (
        <div
            className={clsx(styles.container, {
                [styles.iconLeft]: iconPosition === "left",
                [styles.iconRight]: iconPosition === "right",
            })}
            data-testid={dataTestId}
        >
            {icon && (
                <SVGIcon
                    className={clsx(styles.icon, {
                        [styles.lystAppIcon]: icon === "lyst-app-icon" && variant === "primary",
                    })}
                    name={icon}
                    overrideStyle
                />
            )}
            <Text
                as="span"
                className={clsx(
                    titleClassName,
                    width === "full" && icon ? styles.fullWidth : undefined
                )}
                textStyle={size === "lg" ? "body-s-bold" : "body-s"}
                color={"inherit"}
            >
                {title}
            </Text>
        </div>
    );

    return href ? (
        <a className={clsx(className, buttonClass)} onClick={onClick} href={href}>
            {content}
        </a>
    ) : (
        <button
            className={clsx(className, buttonClass)}
            onClick={onClick}
            disabled={inactive}
            type={type}
        >
            {content}
        </button>
    );
}
